import React from 'react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../i18n/TModules';
import { WizardProperty } from '../wizardModel';
import { wz } from '../wizardStyles';
import { AddButton, RemoveButton } from './controls';
import { SelectField, TextField } from './fields';

const TT = `${TOrbit}.wizard.trigger.transaction`;

// 6778:18577 `props`: a 1.6 rule on the left, 19.6 in, rows 14 apart; 45 % while its box is off.
const Group = styled.div<{ $disabled: boolean }>`
  margin: 0 0 0 8px;
  padding-left: 19.6px;
  border-left: 1.6px solid ${wz.border};
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};

  > * + * {
    margin-top: 14px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;

  > * + * {
    margin-left: 10px;
  }
`;

const EMPTY: WizardProperty = { key: '', operator: 'equals', value: '' };

interface IPropertyConditionsProps {
  value: WizardProperty[];
  onChange: (value: WizardProperty[]) => void;
  disabled: boolean;
}

/** Key / Equals | Not Equals / value rows joined by AND; the FR fixes their type to String. */
const PropertyConditions: React.FC<IPropertyConditionsProps> = ({ value, onChange, disabled }) => {
  const { t } = useMultiLanguageTranslation();
  const change = (index: number, patch: Partial<WizardProperty>) =>
    onChange(value.map((row, at) => (at === index ? { ...row, ...patch } : row)));

  return (
    <Group $disabled={disabled} aria-disabled={disabled} data-testid="orbit-wizard-properties">
      {value.map((row, index) => (
        // Rows have no identity of their own; editing never reorders them.
        <Row key={index}>
          <TextField
            label={t(`${TT}.propertyKey`)}
            placeholder={t(`${TT}.propertyKey`)}
            width="md"
            value={row.key}
            disabled={disabled}
            onChange={key => change(index, { key })}
          />
          <SelectField<WizardProperty['operator']>
            label={t(`${TT}.equals`)}
            value={row.operator}
            width="sm"
            disabled={disabled}
            options={[
              { value: 'equals', label: t(`${TT}.equals`) },
              { value: 'not_equals', label: t(`${TT}.notEquals`) }
            ]}
            onChange={operator => change(index, { operator })}
          />
          <TextField
            label={t(`${TT}.propertyValue`)}
            placeholder={t(`${TT}.propertyValue`)}
            width="md"
            value={row.value}
            disabled={disabled}
            onChange={next => change(index, { value: next })}
          />
          {value.length > 1 && !disabled ? (
            <RemoveButton
              label={t(`${TT}.removeCondition`)}
              onClick={() => onChange(value.filter((_, at) => at !== index))}
            />
          ) : null}
        </Row>
      ))}
      <AddButton
        label={t(`${TT}.addCondition`)}
        disabled={disabled}
        onClick={() => onChange([...value, EMPTY])}
      />
    </Group>
  );
};

export default PropertyConditions;
